import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'

interface IntroScreenProps {
  onComplete: () => void
  minDuration?: number // Minimum duration in milliseconds (default: 3000ms)
}

/**
 * Intro Screen Component - Gold Shine Animation
 * Similar to brilliant.org intro, professional and elegant
 * 
 * Features:
 * - Gold shine animation on text
 * - Logo zoom in effect
 * - Responsive for desktop, mobile, and app
 * - Preloads data during intro animation
 */
const IntroScreen: React.FC<IntroScreenProps> = ({
  onComplete,
  minDuration = 2000
}) => {
  const [isVisible, setIsVisible] = useState(true)
  const [startTime] = useState(Date.now())

  useEffect(() => {
    // Preload critical data during intro
    const preloadData = async () => {
      try {
        // Preload logo image
        const logoImg = new Image()
        logoImg.src = '/AI-PhysicsBook.png'

        // You can add more preloading here:
        // - Preload critical API endpoints
        // - Preload lesson data
        // - Preload user authentication state
        // - etc.

        console.log('📦 Preloading data during intro...')
      } catch (error) {
        console.warn('Preload warning:', error)
      }
    }

    preloadData()

    // Calculate remaining time to meet minimum duration
    const timer = setTimeout(() => {
      const elapsed = Date.now() - startTime
      // We want total time to be minDuration.
      // The fade out takes 300ms (0.3s).
      // So we need to start fading out at minDuration - 300.
      const fadeOutDuration = 300
      const timeUntilFadeOut = Math.max(0, minDuration - fadeOutDuration - elapsed)

      setTimeout(() => {
        setIsVisible(false)
        // Wait for fade out to finish, then complete
        setTimeout(() => {
          onComplete()
        }, fadeOutDuration)
      }, timeUntilFadeOut)
    }, 100) // Small delay to ensure animation starts

    return () => clearTimeout(timer)
  }, [onComplete, minDuration, startTime])

  if (!isVisible) return null

  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-[#fff7e8]"
      style={{
        // Ensure full coverage
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        // Prevent scrolling
        overflow: 'hidden'
      }}
    >
      {/* Logo */}
      <motion.img
        src="/AI-PhysicsBook.png"
        alt="AI-PhysicsBook Logo"
        className="w-[220px] sm:w-[280px] md:w-[320px] mb-5 sm:mb-8"
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.2, ease: 'easeOut' }}
        style={{
          maxWidth: '90%',
          height: 'auto'
        }}
      />

      {/* Text with Gold Shine Effect */}
      <div className="relative inline-block">
        <h1
          className="shine-text text-[32px] sm:text-[42px] md:text-[48px] font-black text-black"
          data-text="Ebook.vatli365.vn"
        >
          Ebook.vatli365.vn
        </h1>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
        .shine-text {
          position: relative;
          display: inline-block;
          transform: scale(1);
          animation: zoomIn 0.8s ease-out forwards;
          user-select: none;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
        }

        @keyframes zoomIn {
          0% {
            transform: scale(0.92);
            opacity: 0;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        .shine-text::before {
          content: attr(data-text);
          position: absolute;
          inset: 0;
          background: linear-gradient(
            90deg,
            #d4b86a,
            #fceabb,
            #d4b86a
          );
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          opacity: 0;
          mask-image: linear-gradient(
            75deg,
            transparent 0%,
            rgba(255, 255, 200, 0.35) 35%,
            rgba(255, 255, 255, 1) 50%,
            rgba(255, 255, 200, 0.35) 65%,
            transparent 100%
          );
          mask-size: 250% 250%;
          mask-position: -150% 0;
          filter: brightness(1.7);
          animation: goldSweep 1.5s ease-out 1;
          animation-delay: 0.1s;
          animation-fill-mode: forwards;
        }

        @keyframes goldSweep {
          0% {
            mask-position: 150% 0;
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          100% {
            mask-position: -150% 0;
            opacity: 1;
          }
        }

        @media (max-width: 640px) {
          .shine-text {
            font-size: 28px;
          }
        }
        `
      }} />
    </motion.div>
  )
}

export default IntroScreen

